import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { map } from 'lit/directives/map.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { CSESlideIn } from '@cloudsuite/mosaic-elements'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import type { PdpPremiumBundle, PdpPremiumBundleProduct } from 'components/discovery/pdp/typing/pdp-product.js'
import { formatGiftPrice } from 'components/discovery/pdp/shared/format-gift-price.js'
import { BASKET_STORE, type BasketStore } from 'components/ordering/order-form/basket-store.js'
import { updatePremiumProduct } from 'components/ordering/order-form/premiums-api.js'
import { ProductAddedEvent } from 'components/ordering/add-to-basket/events.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import 'components/shared/product-preview/product-preview.js'
import { style } from './premium-free-product-slide-in.style.js'

export const premiumFreeProductSlideInHiddenFields = ['image', 'original-price'] as const

@customElement('csc-premium-free-product-slide-in')
export class CSCPremiumFreeProductSlideIn extends HiddenFieldsMixin(LitElement, premiumFreeProductSlideInHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(PDP_PRODUCT_STORE)
  private pdpStore!: PdpProductStore

  @inject(BASKET_STORE)
  private basketStore!: BasketStore

  @property()
  locale = 'en-US'

  @query('cse-slide-in')
  private slideIn?: CSESlideIn

  @state()
  private bundle: PdpPremiumBundle | null = null

  @state()
  private selectedGiftId: number | null = null

  @state()
  private isSaving = false

  private itemId: number | null = null

  private opening: Promise<void> = Promise.resolve()

  private saving: Promise<void> = Promise.resolve()

  private pendingOpen = false

  private itemIdsBeforeLastChange = new Set<string>()

  private currentItemIds = new Set<string>()

  private disposeEffect?: () => void

  override connectedCallback() {
    super.connectedCallback()
    window.addEventListener(ProductAddedEvent.type, this.handleProductAdded)

    this.disposeEffect = createEffect(() => {
      this.itemIdsBeforeLastChange = this.currentItemIds
      this.currentItemIds = new Set(Object.keys(this.basketStore.productsInBasket()))
    })
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener(ProductAddedEvent.type, this.handleProductAdded)

    this.disposeEffect?.()
    delete this.disposeEffect
  }

  override async getUpdateComplete(): Promise<boolean> {
    const result = await super.getUpdateComplete()
    await this.opening
    await this.saving

    return result
  }

  private handleProductAdded = (event: Event): void => {
    const { itemId } = event as ProductAddedEvent
    if (itemId === undefined || this.itemIdsBeforeLastChange.has(String(itemId))) return

    const product = this.pdpStore.product()
    const line = this.basketStore.productsInBasket()[itemId]
    if (!product || line?.productId !== product.id) return

    const premiumBundles = product.premium_bundles
    const firstGift = premiumBundles?.premium_bundle.products[0]
    if (!premiumBundles?.choose_sub_product || !firstGift) return

    this.itemId = itemId
    this.bundle = premiumBundles.premium_bundle
    this.selectedGiftId = firstGift.id
    this.pendingOpen = true
    this.requestUpdate()
  }

  override updated() {
    if (!this.pendingOpen) return

    this.pendingOpen = false
    this.opening = this.openSlideIn()
  }

  private openSlideIn = async (): Promise<void> => {
    const slideIn = this.slideIn
    if (!slideIn) return

    await slideIn.updateComplete
    if (!this.isConnected) return

    await slideIn.open()
  }

  private selectGift = (giftId: number) => () => {
    this.selectedGiftId = giftId
  }

  private confirmChoice = async (): Promise<void> => {
    this.saving = this.saveAndClose()
    await this.saving
  }

  private saveAndClose = async (): Promise<void> => {
    if (this.itemId === null || this.selectedGiftId === null) return

    this.isSaving = true
    try {
      await updatePremiumProduct(this.itemId, this.selectedGiftId)
    } finally {
      this.isSaving = false
    }
    await this.slideIn?.close()
  }

  private renderFooter = () => html`
    <div class="footer" slot="footer">
      <cse-button variant="primary" fit="fill" ?loading="${this.isSaving}" @click="${this.confirmChoice}">
        ${t('confirm_choice', {
          ns: 'csc-premium-free-product-slide-in',
          default: 'Confirm free product',
        })}
      </cse-button>
    </div>
  `

  private renderGift = (gift: PdpPremiumBundleProduct) => html`
    <cse-list-item>
      <cse-selectable
        size="small"
        fit="fill"
        state="${this.selectedGiftId === gift.id ? 'selected' : 'selectable'}"
        @click="${this.selectGift(gift.id)}"
      >
        <div class="gift">
          <cse-radio-button value="${gift.id}" .checked="${this.selectedGiftId === gift.id}"></cse-radio-button>
          <csc-product-preview
            product-name="${gift.name}"
            image-source="${gift.picture.small}"
            hidden-fields="${this.isHidden('image') ? 'image' : ''}"
            product-id="${gift.code}"
            price-lead="${this.isHidden('original-price') ? '' : formatGiftPrice(gift, this.locale)}"
            price-variant="sale"
            price="${t('price', { ns: 'csc-premium-free-product-slide-in', default: 'Free' })}"
          ></csc-product-preview>
        </div>
      </cse-selectable>
    </cse-list-item>
  `

  override render() {
    if (!this.bundle) return nothing

    const addedProduct = this.pdpStore.product()?.name ?? ''

    return html`
      <cse-slide-in>
        <div slot="header">
          <cse-text as="h4" variant="heading-4">
            ${t('title', {
              ns: 'csc-premium-free-product-slide-in',
              default: 'Choose your free product',
            })}
          </cse-text>
        </div>
        <cse-alert variant="success">
          <cse-text>
            ${t('alert_success_before', { ns: 'csc-premium-free-product-slide-in', default: '' })}
            <strong class="csc-premium-free-product-slide-in__added-product">${addedProduct}</strong>
            ${t('alert_success_after', {
              ns: 'csc-premium-free-product-slide-in',
              default: 'has been added to you shopping basket.',
            })}
          </cse-text>
        </cse-alert>
        <cse-text>${this.bundle.description}</cse-text>
        <cse-list>
          ${map(this.bundle.products, this.renderGift)}
        </cse-list>
        ${this.renderFooter()}
      </cse-slide-in>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-premium-free-product-slide-in': CSCPremiumFreeProductSlideIn
  }
}
